import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { LOGO_URL } from "@/lib/useReveal";
import { Field, Input, Button, StatusPill } from "@/admin/ui";
import { formatApiError } from "@/user/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function InvitationAccept() {
    const { token } = useParams();
    const nav = useNavigate();
    const [inv, setInv] = useState(null);
    const [company, setCompany] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [pw, setPw] = useState("");
    const [pw2, setPw2] = useState("");
    const [accept, setAccept] = useState(false);
    const [busy, setBusy] = useState(false);
    const [done, setDone] = useState(false);

    useEffect(() => {
        axios.get(`${API}/invitations/verify`, { params: { token } })
            .then((r) => { setInv(r.data.invitation); setCompany(r.data.company); })
            .catch((e) => setErr(formatApiError(e)))
            .finally(() => setLoading(false));
    }, [token]);

    async function activate(e) {
        e.preventDefault();
        setErr("");
        if (pw.length < 8) return setErr("Password must be at least 8 characters.");
        if (pw !== pw2) return setErr("Passwords do not match.");
        if (!accept) return setErr("You must accept the company policy.");
        setBusy(true);
        try {
            await axios.post(`${API}/invitations/accept`, { token, password: pw, accept_policy: true });
            setDone(true);
            setTimeout(() => nav("/login", { replace: true }), 1500);
        } catch (e2) {
            setErr(formatApiError(e2));
        } finally {
            setBusy(false);
        }
    }

    return (
        <div data-testid="invitation-page" className="dot-grid min-h-screen w-full relative flex items-center justify-center px-6 py-16">
            <div className="pointer-events-none absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 h-[560px] w-[820px] max-w-[95vw] rounded-full opacity-60 anim-pulse-glow"
                 style={{ background: "radial-gradient(closest-side, rgba(139,92,246,0.28), transparent 70%)" }} />

            <div className="relative w-full max-w-md">
                <div className="flex justify-center">
                    <img src={LOGO_URL} alt="" className="logo-blend h-20 w-20 object-contain anim-float-slow" style={{ filter: "drop-shadow(0 20px 40px rgba(139,92,246,0.35))" }} />
                </div>

                {loading ? (
                    <div className="mt-8 glass-strong rounded-3xl p-8 h-[380px] animate-pulse" />
                ) : err && !inv ? (
                    <div className="mt-8 glass-strong rounded-3xl p-8 text-center">
                        <div className="font-display text-2xl text-black">Invitation issue</div>
                        <p className="mt-3 text-[14px] text-rose-600">{err}</p>
                        <Link to="/" className="mt-6 inline-block text-[13px] text-violet-600 hover:underline">← Back to Landing</Link>
                    </div>
                ) : done ? (
                    <div className="mt-8 glass-strong rounded-3xl p-8 text-center">
                        <div className="mx-auto h-12 w-12 rounded-full bg-emerald-50 text-emerald-600 inline-flex items-center justify-center">
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                                <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </div>
                        <div className="mt-4 font-display text-2xl text-black">Account activated</div>
                        <p className="mt-2 text-[14px] text-neutral-600">Redirecting you to the login page…</p>
                    </div>
                ) : (
                    <>
                        <h1 className="mt-6 text-center font-display text-3xl sm:text-4xl font-light tracking-tight text-black">
                            Welcome{inv?.name ? `, ${inv.name.split(" ")[0]}` : ""}.
                        </h1>
                        <p className="mt-2 text-center text-[13.5px] text-neutral-600">
                            You’ve been invited to join <b>{company?.name || "your company"}</b> as{" "}
                            <span className="inline-flex items-center gap-1"><StatusPill status={inv?.role} /></span>
                        </p>

                        <form onSubmit={activate} className="mt-6 glass-strong rounded-3xl p-6 sm:p-8 space-y-4">
                            <Field label="Email"><Input value={inv?.email || ""} readOnly /></Field>
                            <Field label="Create Password">
                                <Input type="password" required minLength={8} value={pw} onChange={(e) => setPw(e.target.value)} data-testid="inv-accept-pw" />
                            </Field>
                            <Field label="Confirm Password">
                                <Input type="password" required minLength={8} value={pw2} onChange={(e) => setPw2(e.target.value)} data-testid="inv-accept-pw2" />
                            </Field>
                            <label className="flex items-start gap-2 text-[13px] text-neutral-700 cursor-pointer">
                                <input type="checkbox" checked={accept} onChange={(e) => setAccept(e.target.checked)} className="mt-1" data-testid="inv-accept-policy" />
                                <span>I accept the <span className="text-violet-600">company policy</span> and CEO’s GPT terms of use.</span>
                            </label>
                            {err && <div className="rounded-xl border border-rose-200 bg-rose-50/70 text-rose-700 text-[13px] px-3.5 py-2.5">{err}</div>}
                            <Button type="submit" size="lg" disabled={busy} data-testid="inv-accept-submit" className="w-full justify-center">
                                {busy ? "Activating…" : "Activate Account"}
                            </Button>
                        </form>
                    </>
                )}
            </div>
        </div>
    );
}
